import { Link } from "react-router-dom";
import { ArrowRight, Check, MapPin, Phone } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { JsonLd } from "../components/JsonLd";
import { BOOKING_URL, CONTACT } from "../data/site";
import { track } from "../lib/analytics";

const SITE = "https://zentryxsystems.com";
const primaryBtn = "inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-emerald hover:bg-emerald-dark text-white font-semibold transition-colors";
const secondaryBtn = "inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-white hover:bg-slate-50 text-navy font-semibold border border-slate-200 transition-colors";

const CRUMB = {
  "law-firms": "Law Firms",
  "healthcare-practices": "Healthcare Practices",
  "professional-services": "Professional Services",
  "financial-services": "Financial Services",
};

// Key internal links present on every industry page (topical authority).
const KEY_LINKS = [
  { to: "/services#managed-it", label: "Managed IT Services" },
  { to: "/services#cybersecurity", label: "Cybersecurity Services" },
  { to: "/services#microsoft-365", label: "Microsoft 365 Support" },
  { to: "/services#cloud", label: "Cloud Solutions" },
  { to: "/about", label: "About Zentryx Systems" },
  { to: "/managed-it-services-atlanta", label: "Atlanta Managed IT overview" },
];

export default function IndustryLandingPage({ data, slug }) {
  const path = `/industries/${slug}`;
  const Icon = data.icon;
  const bookClick = (loc) => track("book_intro_call_click", { location: `${path}:${loc}` });

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: data.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
      { "@type": "ListItem", position: 2, name: "Industries", item: `${SITE}/industries` },
      { "@type": "ListItem", position: 3, name: data.h1, item: `${SITE}${path}` },
    ],
  };
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: data.h1,
    provider: {
      "@type": "LocalBusiness",
      name: "Zentryx Systems",
      telephone: "+1-404-860-2291",
      email: CONTACT.email,
      address: { "@type": "PostalAddress", addressLocality: "Atlanta", addressRegion: "GA", addressCountry: "US" },
      url: SITE,
    },
    areaServed: data.areas,
    url: `${SITE}${path}`,
    description: data.metaDescription,
  };
  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: data.metaTitle,
    description: data.metaDescription,
    url: `${SITE}${path}`,
    inLanguage: "en-US",
    isPartOf: { "@type": "WebSite", name: "Zentryx Systems", url: SITE },
  };

  return (
    <div data-testid={`industry-page-${slug}`}>
      <Seo title={data.metaTitle} description={data.metaDescription} path={path} image={data.image} />
      <JsonLd data={faqSchema} id={`faq-${slug}`} />
      <JsonLd data={breadcrumb} id={`bc-${slug}`} />
      <JsonLd data={serviceSchema} id={`svc-${slug}`} />
      <JsonLd data={webPageSchema} id={`wp-${slug}`} />

      {/* HERO */}
      <section className="pt-32 sm:pt-40 pb-16 px-5 sm:px-8 bg-mist">
        <nav aria-label="Breadcrumb" className="max-w-7xl mx-auto mb-8">
          <ol className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
            <li><Link to="/" className="hover:text-navy transition-colors">Home</Link></li>
            <li aria-hidden="true" className="text-slate-300">/</li>
            <li><Link to="/industries" className="hover:text-navy transition-colors">Industries</Link></li>
            <li aria-hidden="true" className="text-slate-300">/</li>
            <li className="text-charcoal font-medium" aria-current="page">{CRUMB[slug] || data.eyebrow}</li>
          </ol>
        </nav>
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-teal bg-teal/10 px-4 py-1.5 rounded-full mb-6">
              <Icon className="h-4 w-4" /> {data.eyebrow}
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-charcoal leading-[1.08]">
              {data.h1}
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">{data.lead}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link to="/contact" data-testid="hero-assessment-cta" className={primaryBtn}>
                Request a Free IT Assessment <ArrowRight className="h-4 w-4" />
              </Link>
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer"
                onClick={() => bookClick("hero")} data-testid="hero-booking-cta" className={secondaryBtn}>
                Book an Intro Call
              </a>
            </div>
            <p className="mt-5 inline-flex items-center gap-2 text-sm text-slate-500">
              <MapPin className="h-4 w-4 text-navy" /> Serving {data.areaShort} across metro Atlanta
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <img src={data.image} alt={data.imageAlt || data.h1} width="1200" height="440" loading="eager"
              className="rounded-3xl w-full h-[440px] object-cover shadow-2xl shadow-slate-300/50" />
          </Reveal>
        </div>
      </section>

      {/* INTRO / OVERVIEW */}
      <section className="py-20 sm:py-28 px-5 sm:px-8">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-charcoal mb-6">{data.overviewH2}</h2>
            {data.intro.map((p) => (
              <p key={p} className="text-lg text-slate-600 leading-relaxed mb-5">{p}</p>
            ))}
          </Reveal>
        </div>
      </section>

      {/* WHO WE SERVE (optional) */}
      {data.audience && (
        <section className="pb-4 px-5 sm:px-8">
          <div className="max-w-7xl mx-auto">
            <Reveal className="max-w-2xl mb-8">
              <p className="text-sm uppercase tracking-widest font-semibold text-teal mb-4">Who we serve</p>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-charcoal">{data.audience.title}</h2>
              <p className="mt-4 text-slate-600 leading-relaxed">{data.audience.intro}</p>
            </Reveal>
            <div className="flex flex-wrap gap-2.5 mb-4">
              {data.audience.items.map((t) => (
                <span key={t} className="px-4 py-2 rounded-full text-sm font-medium bg-mist text-charcoal border border-slate-200">{t}</span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CHALLENGES */}
      <section className="py-20 sm:py-28 px-5 sm:px-8 bg-mist">
        <div className="max-w-7xl mx-auto">
          <Reveal className="max-w-2xl mb-14">
            <p className="text-sm uppercase tracking-widest font-semibold text-teal mb-4">Challenges we solve</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-charcoal">{data.challengesH2}</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {data.challenges.map((c, i) => (
              <Reveal key={c.title} delay={(i % 4) * 0.06}>
                <div className="h-full rounded-3xl border border-slate-200 bg-white p-7">
                  <div className="h-11 w-11 rounded-2xl bg-navy/5 flex items-center justify-center mb-5">
                    <c.icon className="h-5 w-5 text-navy" />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-charcoal mb-2">{c.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SOLUTIONS */}
      <section className="py-20 sm:py-28 px-5 sm:px-8">
        <div className="max-w-7xl mx-auto">
          <Reveal className="max-w-2xl mb-14">
            <p className="text-sm uppercase tracking-widest font-semibold text-teal mb-4">How Zentryx helps</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-charcoal">{data.solutionsH2}</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.solutions.map((s, i) => (
              <Reveal key={s.name} delay={(i % 3) * 0.06}>
                <div className="h-full rounded-3xl border border-slate-200 bg-white p-7 hover:shadow-lg hover:shadow-slate-200/60 transition-all">
                  <div className="h-11 w-11 rounded-2xl bg-teal/10 flex items-center justify-center mb-5">
                    <s.icon className="h-5 w-5 text-teal" />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-charcoal mb-2">{s.name}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <Link to="/services" className="mt-10 inline-flex items-center gap-2 font-semibold text-navy hover:gap-3 transition-all">
              Explore all managed IT services <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* SECURITY */}
      <section className="pb-4 px-5 sm:px-8">
        <div className="max-w-7xl mx-auto rounded-3xl bg-navy relative overflow-hidden px-8 sm:px-14 py-14 sm:py-16">
          <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-teal/20 blur-3xl" />
          <div className="relative grid lg:grid-cols-2 gap-10 items-start">
            <Reveal>
              <p className="text-sm uppercase tracking-widest font-semibold text-teal mb-4">Security &amp; data protection</p>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white tracking-tight">{data.security.h2}</h2>
              <p className="mt-5 text-slate-300 leading-relaxed">{data.security.intro}</p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                {data.security.points.map((p) => (
                  <div key={p} className="flex items-start gap-3">
                    <span className="mt-0.5 h-5 w-5 rounded-full bg-emerald/20 flex items-center justify-center flex-shrink-0">
                      <Check className="h-3 w-3 text-emerald" />
                    </span>
                    <span className="text-sm text-slate-200">{p}</span>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-xs text-slate-400 leading-relaxed">{data.security.disclaimer}</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHY ZENTRYX */}
      <section className="py-20 sm:py-28 px-5 sm:px-8">
        <div className="max-w-7xl mx-auto">
          <Reveal className="max-w-2xl mb-14">
            <p className="text-sm uppercase tracking-widest font-semibold text-teal mb-4">Why Zentryx</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-charcoal">{data.whyH2}</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.why.map((w, i) => (
              <Reveal key={w.title} delay={(i % 3) * 0.06}>
                <div className="h-full rounded-3xl bg-mist border border-slate-200 p-7">
                  <h3 className="font-heading text-lg font-bold text-charcoal mb-2">{w.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{w.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="pb-20 sm:pb-24 px-5 sm:px-8">
        <div className="max-w-3xl mx-auto">
          <Reveal className="mb-10">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-charcoal">Frequently asked questions</h2>
          </Reveal>
          <Reveal>
            <Accordion type="single" collapsible className="space-y-4">
              {data.faqs.map((f, i) => (
                <AccordionItem key={f.q} value={`faq-${i}`} data-testid={`faq-item-${i}`}
                  className="rounded-2xl border border-slate-200 bg-white px-6">
                  <AccordionTrigger className="font-heading font-semibold text-charcoal text-left hover:no-underline py-5">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 leading-relaxed pb-5">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>

          {/* RELATED / INTERNAL LINKS */}
          <Reveal>
            <div className="mt-14 rounded-3xl bg-mist border border-slate-200 p-8">
              <h3 className="font-heading text-lg font-bold text-charcoal mb-5">Explore more</h3>
              <div className="grid sm:grid-cols-2 gap-x-10 gap-y-6">
                <div>
                  <p className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-3">Services</p>
                  <ul className="space-y-3">
                    {KEY_LINKS.map((r) => (
                      <li key={r.to}>
                        <Link to={r.to} className="inline-flex items-center gap-2 text-navy font-medium hover:gap-3 transition-all">
                          <ArrowRight className="h-4 w-4 flex-shrink-0" /> {r.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-3">Related</p>
                  <ul className="space-y-3">
                    {data.related.map((r) => (
                      <li key={r.to}>
                        <Link to={r.to} className="inline-flex items-center gap-2 text-navy font-medium hover:gap-3 transition-all">
                          <ArrowRight className="h-4 w-4 flex-shrink-0" /> {r.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="pb-24 sm:pb-32 px-5 sm:px-8">
        <div className="max-w-5xl mx-auto rounded-3xl bg-navy relative overflow-hidden px-8 sm:px-16 py-16 text-center">
          <div className="absolute -left-16 -bottom-16 h-56 w-56 rounded-full bg-emerald/20 blur-3xl" />
          <Reveal className="relative">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white tracking-tight">{data.ctaTitle}</h2>
            <p className="mt-4 text-lg text-slate-300 max-w-2xl mx-auto">{data.ctaBody}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer"
                onClick={() => bookClick("footer")} data-testid="cta-booking-cta" className={primaryBtn}>
                Book an Intro Call <ArrowRight className="h-4 w-4" />
              </a>
              <Link to="/contact" data-testid="cta-assessment-cta"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold border border-white/20 transition-colors">
                Request a Free IT Assessment
              </Link>
            </div>
            <a href={CONTACT.phoneHref} className="mt-6 inline-flex items-center gap-2 text-slate-300 hover:text-white transition-colors text-sm">
              <Phone className="h-4 w-4" /> Or call {CONTACT.phone}
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
